import {
  Component, Inject, EventEmitter, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import Cropper from 'cropperjs';
import { takeUntil } from 'rxjs/operators';

import { UserProfileHttpService, UserProfileImageRequest } from '@sondermind/data-access/therapist-profile';
import { BreakpointsService } from '@sondermind/utilities/services';

import { MODAL_TEMPLATE_DATA } from '../modal/models/modal.model';

import { ImageUploaderData } from './models/image-uploader.model';

@Component({
  selector: 'sondermind-image-uploader',
  templateUrl: 'image-uploader.component.html',
  styleUrls: ['image-uploader.component.scss'],
  standalone: false
})
export class ImageUploaderComponent implements OnInit, AfterViewInit, OnDestroy {
  imageRequest: UserProfileImageRequest;
  imgNameRegExp: RegExp = /[^/]+\.\w{3,4}(?=([?&].*$|$))/;

  @ViewChild('imageUploadInput')
  private fileElement: ElementRef;
  private cropper: Cropper;
  // Janky but simple way to ignore cropper's initial events to know
  // WHen to stop displaying help text
  cropperEventCntr: number;
  showHelpText: boolean;

  @ViewChild('cropimage')
    imageElement: ElementRef;

  destroyed$ = new EventEmitter<void>();

  constructor(
    // TODO: create image-uploader-modal so that this component isn't aware that it's in a modal
    @Inject(MODAL_TEMPLATE_DATA) public data: ImageUploaderData,
    private userProfileHttpService: UserProfileHttpService,
    private snackBar: MatSnackBar,
    private bpService: BreakpointsService
  ) {}

  ngOnInit(): void {
    this.cropperEventCntr = 0;
    this.showHelpText = true;

    this.data.imageUploadEmitter$.pipe(
      takeUntil(this.destroyed$)
    )
      .subscribe((data) => {
        this.selectPhoto(data);
      });

    this.data.saveClickedEmitter$.pipe(
      takeUntil(this.destroyed$)
    )
      .subscribe((_) => {
        this.data.saveEmitter$.emit(this.imageRequest);
      });
  }

  ngAfterViewInit(): void {
    this.bpService.isMobile$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isMobile) => {
        let mobileCropperProperties = {};
        if (isMobile) {
          mobileCropperProperties = {
            ...mobileCropperProperties,
            dragMode: 'move',
            cropBoxResizable: false,
            zoomable: true,
            data: null
          };
        }
        if (this.data.image.src) {
          this.getPreviousImageData(this.data.image.src);
        }
        this.cropper = new Cropper(this.imageElement.nativeElement, {
          viewMode: 2,
          aspectRatio: 1,
          highlight: false,
          checkCrossOrigin: false,
          checkOrientation: false,
          toggleDragModeOnDblclick: false,
          zoomable: false,
          guides: false,
          center: false,
          data: {
            x: Number(this.data.image.x),
            y: Number(this.data.image.y),
            width: Number(this.data.image.width),
            height: Number(this.data.image.height),
            rotate: 0,
            scaleX: 1,
            scaleY: 1
          },
          ...mobileCropperProperties,
          crop: (event) => this.onCrop(event)
        });
      });
  }

  selectPhoto(data: any): void {
    this.fileElement.nativeElement.click();
  }

  photoChosen(event: any): void {
    if (this.fileElement.nativeElement.files[0] && this.fileElement.nativeElement.files.length && this.isValidDoc(this.fileElement.nativeElement.files[0].type)) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = e.target.result;
        const imageName = this.fileElement.nativeElement.files[0].name;
        this.data.image.src = base64;
        this.cropper.replace(base64);
        this.imageRequest = new UserProfileImageRequest(base64, imageName);
        this.data.actionBtnStatus$.emit({ style: 'primary', status: false });
      };
      reader.readAsDataURL(this.fileElement.nativeElement.files[0]);
      this.showHelpText = true;
      this.cropperEventCntr = 0;
    } else {
      this.snackBar.open('Please select a PNG or JPG image.', 'Dismiss');
    }
  }

  cancelUpload(event): void {
    this.data.saveEmitter$.emit('close');
  }

  private isValidDoc(docType: string): boolean {
    return ['image/jpeg', 'image/jpg', 'image/png'].includes(docType);
  }

  private onCrop(event): void {
    this.cropperEventCntr += 1;

    // Cropper in the state we use it always fires three events at startup
    if (this.cropperEventCntr > 3) {
      this.showHelpText = false;
    }

    this.cropper.getCroppedCanvas({ maxWidth: 800 });
    this.userProfileHttpService.updateCoords(event.detail.height, event.detail.width, event.detail.x, event.detail.y);
  }

  /**
     * Gets the blob of a previously uploaded image by re-requesting it from
     * the server, converting it to a base64 string, and then instantiating the
     * `UserProfileImageRequest` on the class.
     *
     * @private
     * @param {string} imgSrc
     * @memberof ImageUploaderComponent
     */
  private getPreviousImageData(imgSrc: string): void {
    this.userProfileHttpService.getBlobImage(imgSrc)
      .subscribe((blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const imageName = this.imgNameRegExp.exec(imgSrc);
          this.imageRequest = new UserProfileImageRequest(base64, imageName[0]);
          this.data.actionBtnStatus$.emit({ style: 'primary', status: false });
        };
        reader.readAsDataURL(blob);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }
}
